"use client";

import { memo, useMemo, useState } from "react";
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from "victory";

import { TimePeriod } from "../../types";

const COLORS = {
  lineStroke: "#2BA682", // Teal line color
  areaFill: "rgba(43, 166, 130, 0.08)", // Light teal area fill
  pointFill: "#FFFFFF", // White fill for data points
  pointStroke: "#2BA682", // Teal stroke for data points

  // Axis/text colors
  axisText: "#1D1934", // Dark text for axis labels

  // Grid line color
  gridLine: "#D2D1D6", // Dashed grid lines
};

type SearchAppearancesChartProps = {
  data: Array<{ period: string; totalCount: number }>;
  timePeriod: TimePeriod;
};

const SearchAppearancesChart: React.FC<SearchAppearancesChartProps> = memo(
  ({ data, timePeriod }) => {
    const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

    const chartData = useMemo(() => {
      if (!data || data.length === 0) {
        return [];
      }
      return data.map((item, index) => ({
        x: index + 1,
        y: item.totalCount,
      }));
    }, [data]);

    // Calculate dynamic y-axis tick values based on data
    const { yTickValues, yMax } = useMemo(() => {
      if (!data || data.length === 0) {
        return { yTickValues: [0, 50, 100, 150, 200, 250], yMax: 250 };
      }

      const maxValue = Math.max(...data.map((item) => item.totalCount));

      if (maxValue === 0) {
        return { yTickValues: [0, 25, 50, 75, 100], yMax: 100 };
      }

      const paddedMax = maxValue * 1.1;

      const roughStep = paddedMax / 5;

      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const residual = roughStep / magnitude;

      let niceStep: number;
      if (residual <= 1) {
        niceStep = 1 * magnitude;
      } else if (residual <= 2) {
        niceStep = 2 * magnitude;
      } else if (residual <= 2.5) {
        niceStep = 2.5 * magnitude;
      } else if (residual <= 5) {
        niceStep = 5 * magnitude;
      } else {
        niceStep = 10 * magnitude;
      }

      const niceMax = Math.ceil(paddedMax / niceStep) * niceStep;

      const ticks: number[] = [];
      for (let i = 0; i <= niceMax; i += niceStep) {
        ticks.push(Math.round(i));
      }

      return { yTickValues: ticks, yMax: niceMax };
    }, [data]);

    if (!data || data.length === 0 || chartData.length === 0) {
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-[#59566C]">No chart data available</p>
        </div>
      );
    }

    const formatXAxisLabel = (periodIndex: number) => {
      const item = data[periodIndex - 1];
      if (!item) return "";

      const period = item.period;
      const dashIndex = period.indexOf(" - ");
      if (dashIndex !== -1) {
        const firstPart = period.substring(0, dashIndex + 2);
        const secondPart = period.substring(dashIndex + 3);
        return `${firstPart}\n${secondPart}`;
      }
      return period;
    };

    return (
      <VictoryChart
        key={timePeriod}
        containerComponent={<VictoryContainer responsive />}
        height={242}
        width={413}
        padding={{ left: 26, right: 2, top: 16, bottom: 40 }}
        domain={{ y: [0, yMax] }}
        domainPadding={{ x: 24 }}
      >
        <VictoryAxis
          dependentAxis
          tickValues={yTickValues}
          tickFormat={(t) => t.toString()}
          style={{
            axis: {
              stroke: COLORS.gridLine,
              strokeWidth: 1,
              strokeDasharray: "2, 2",
            },
            tickLabels: {
              fill: COLORS.axisText,
              fontSize: 12,
              fontWeight: 500,
              padding: 6,
              lineHeight: 16,
              fontFamily: "Noto Sans, sans-serif",
            },
            grid: {
              stroke: COLORS.gridLine,
              strokeWidth: 1,
              strokeDasharray: "2, 2",
            },
            ticks: { stroke: "transparent" },
          }}
        />

        <VictoryAxis
          tickValues={chartData.map((d) => d.x)}
          tickFormat={(t) => formatXAxisLabel(t)}
          style={{
            axis: { stroke: "transparent" },
            tickLabels: {
              fill: COLORS.axisText,
              fontSize: 12,
              fontWeight: 500,
              padding: 6,
              lineHeight: 1.33,
              fontFamily: "Noto Sans, sans-serif",
            },
            ticks: { stroke: "transparent" },
          }}
        />

        <VictoryArea
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: {
              fill: COLORS.areaFill,
              stroke: "transparent",
            },
          }}
          animate={{
            duration: 800,
            onLoad: { duration: 500 },
          }}
        />

        <VictoryLine
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: {
              stroke: COLORS.lineStroke,
              strokeWidth: 2,
            },
          }}
          animate={{
            duration: 800,
            onLoad: { duration: 500 },
          }}
        />

        <VictoryScatter
          data={chartData}
          size={5}
          style={{
            data: {
              fill: COLORS.lineStroke,
              stroke: "none",
              cursor: "pointer",
            },
          }}
          animate={{
            duration: 800,
            onLoad: { duration: 500 },
          }}
          labels={({ datum }) => (hoveredPoint === datum.x ? datum.y : "")}
          labelComponent={
            <VictoryLabel
              dy={-12}
              style={{
                fill: COLORS.axisText,
                fontSize: 12,
                fontWeight: 500,
                fontFamily: "Noto Sans, sans-serif",
              }}
            />
          }
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseEnter: (_evt, { datum }) => {
                  setHoveredPoint(datum.x);
                  return [];
                },
                onMouseLeave: () => {
                  setHoveredPoint(null);
                  return [];
                },
              },
            },
          ]}
        />
      </VictoryChart>
    );
  },
);

SearchAppearancesChart.displayName = "SearchAppearancesChart";

export default SearchAppearancesChart;
